import React, { useContext } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout/Layout';
import Schedule from './pages/Schedule';
import Dashboard from './pages/Dashboard/Dashboard';
import AddSale from './pages/AddSale/AddSale';
import { Box, Typography, Paper } from '@mui/material';
import { AuthContext } from './contexts/AuthContext';
import { SnackbarProvider } from 'notistack';
import Payroll from './pages/Payroll';
import GoalsPage from './pages/Goals/GoalsPage';
import AddProduct from './pages/Products/AddProduct';
import ProductsList from './pages/Products/ProductsList';
import ProductDetails from './pages/Products/ProductDetails';
import ProductTransfers from './pages/Products/ProductTransfers';
import Orders from './pages/Orders/Orders';
import Workshop from './pages/Workshop/Workshop';
import History from './pages/History/History';
import Login from './components/Login';
import UserManagement from './components/Users/UserManagement';
import Settings from './pages/Settings';
import ErrorReports from './pages/ErrorReports';
import SalesPage from './pages/Sales/SalesPage';
import Clients from './pages/Clients/Clients';
import Leads from './pages/Leads/Leads';
import ContactsPage from './pages/Contacts/ContactsPage';
import TasksPage from './pages/Tasks/TasksPage';
import AddClient from './pages/AddClient/AddClient';
import EditClient from './pages/Clients/EditClient';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { pl } from 'date-fns/locale';
import { ThemeProvider } from '@mui/material/styles';
import theme from './theme';
import './styles/darkTheme.css';
import ProtectedRoute from './components/ProtectedRoute';

// Placeholder component
const PlaceholderPage = ({ title }) => (
  <Paper sx={{ p: 3, height: '100%' }}>
    <Typography variant="h4" sx={{ mb: 2, fontFamily: 'Cinzel, serif' }}>
      {title}
    </Typography>
    <Typography variant="body1" color="text.secondary">
      Ta sekcja jest w trakcie implementacji...
    </Typography>
  </Paper>
);

function App() {
  const { currentUser } = useContext(AuthContext);

  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={pl}>
        <SnackbarProvider maxSnack={3}>
          <Box sx={{ display: 'flex', minHeight: '100vh', overflow: 'visible' }} className="dark-theme">
            <Routes>
              <Route 
                path="/login" 
                element={
                  currentUser ? (
                    <Navigate to="/dashboard" replace />
                  ) : (
                    <Login />
                  )
                } 
              />
              
              <Route element={<Layout />}>
                {/* Dashboard */}
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                
                {/* CRM Routes */}
                <Route
                  path="/leads"
                  element={
                    <ProtectedRoute>
                      <Leads />
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="/clients"
                  element={
                    <ProtectedRoute>
                      <Clients />
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="/add-client"
                  element={
                    <ProtectedRoute>
                      <AddClient />
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="/clients/edit/:clientId"
                  element={
                    <ProtectedRoute>
                      <EditClient />
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="/transactions"
                  element={
                    <ProtectedRoute>
                      <PlaceholderPage title="Sprzedaże" />
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="/contacts"
                  element={
                    <ProtectedRoute>
                      <ContactsPage />
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="/tasks"
                  element={
                    <ProtectedRoute>
                      <TasksPage />
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="/goals"
                  element={
                    <ProtectedRoute>
                      <GoalsPage />
                    </ProtectedRoute>
                  }
                />
                
                {/* Products */}
                <Route
                  path="/products"
                  element={
                    <ProtectedRoute>
                      <ProductsList />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/products/:id"
                  element={
                    <ProtectedRoute>
                      <ProductDetails />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/products/edit/:id"
                  element={
                    <ProtectedRoute>
                      <AddProduct mode="edit" />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/add-product"
                  element={
                    <ProtectedRoute>
                      <AddProduct />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/add-sale"
                  element={
                    <ProtectedRoute>
                      <AddSale />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/transfers"
                  element={
                    <ProtectedRoute>
                      <ProductTransfers />
                    </ProtectedRoute>
                  }
                />

                {/* Sales */}
                <Route
                  path="/sales"
                  element={
                    <ProtectedRoute>
                      <SalesPage />
                    </ProtectedRoute>
                  }
                />

                {/* History */}
                <Route 
                  path="/history" 
                  element={
                    <ProtectedRoute requireAdmin>
                      <History />
                    </ProtectedRoute>
                  } 
                />

                {/* Schedule */}
                <Route
                  path="/schedule"
                  element={
                    <ProtectedRoute>
                      <Schedule />
                    </ProtectedRoute>
                  }
                />

                {/* Payroll */}
                <Route
                  path="/payroll"
                  element={
                    <ProtectedRoute requireAdmin>
                      <Payroll />
                    </ProtectedRoute>
                  }
                />

                {/* Users */}
                <Route
                  path="/users"
                  element={
                    <ProtectedRoute requireAdmin>
                      <UserManagement />
                    </ProtectedRoute>
                  }
                />

                {/* Orders */}
                <Route
                  path="/orders"
                  element={
                    <ProtectedRoute>
                      <Orders />
                    </ProtectedRoute>
                  }
                />

                {/* Workshop */}
                <Route
                  path="/workshop"
                  element={
                    <ProtectedRoute>
                      <Workshop />
                    </ProtectedRoute>
                  }
                />

                {/* Settings */}
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute requireAdmin>
                      <Settings />
                    </ProtectedRoute>
                  }
                />

                {/* Error Reports */}
                <Route
                  path="/error-reports"
                  element={
                    <ProtectedRoute requireAdmin>
                      <ErrorReports />
                    </ProtectedRoute>
                  }
                />

                {/* Default redirect */}
                <Route
                  path="*"
                  element={<Navigate to="/dashboard" replace />}
                />
              </Route>
            </Routes>
          </Box>
        </SnackbarProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
}

export default App;
